/**
 * Product Information Management
 * Handles product description toggle functionality
 */

// Product Description Toggle - Standalone initialization
// This ensures the feature works even when product-options.js is not loaded
document.addEventListener("DOMContentLoaded", function () {
    // Skip if already initialized by ProductOptionsManager
    if (window.productDescriptionToggleInitialized) return;

    // Initialize immediately
    initializeProductDescriptionToggle();
    window.productDescriptionToggleInitialized = true;
});

// Also listen for quick view events to re-initialize when needed
document.addEventListener(
    "quick-view:open",
    function (event) {
        // Wait for the content to be fully loaded
        setTimeout(() => {
            // Check if description toggle is needed in quick view
            const descriptionElement =
                document.querySelector(
                    ".product-short-description"
                );
            if (
                descriptionElement &&
                !descriptionElement.closest(
                    ".product-description-toggle"
                )
            ) {
                initializeProductDescriptionToggle();
            }
        }, 300);
    }
);

/**
 * Initialize product description toggle functionality
 */
function initializeProductDescriptionToggle() {
    const descriptionElement = document.querySelector(
        ".product-short-description"
    );
    if (!descriptionElement) return;

    // Skip if already wrapped by max-height component (text-max-height.liquid)
    if (descriptionElement.closest('max-height')) {
        return;
    }

    // Skip if already wrapped by product-description-toggle
    if (descriptionElement.closest('.product-description-toggle')) {
        return;
    }

    // Get settings from meta tags or theme object
    let enableToggle = false;
    let characterLimit = 150;

    // Try to get from theme object first
    if (
        window.theme?.settings
            ?.enable_description_toggle !== undefined
    ) {
        enableToggle =
            window.theme.settings.enable_description_toggle;
        characterLimit =
            parseInt(
                window.theme.settings
                    .description_character_limit
            ) || 150;
    } else {
        // Fallback: get from meta tags or data attributes
        const metaToggle = document.querySelector(
            'meta[name="description-toggle-enabled"]'
        );
        const metaLimit = document.querySelector(
            'meta[name="description-character-limit"]'
        );

        if (metaToggle)
            enableToggle = metaToggle.content === "true";
        if (metaLimit)
            characterLimit =
                parseInt(metaLimit.content) || 150;
    }

    if (!enableToggle) return;

    const descriptionText =
        descriptionElement.textContent ||
        descriptionElement.innerText;

    // Only add toggle if text exceeds character limit
    if (descriptionText.length <= characterLimit) return;

    setupProductDescriptionToggle(
        descriptionElement,
        characterLimit
    );
}

/**
 * Setup product description toggle with truncated content and toggle button
 * @param {HTMLElement} descriptionElement - The description element to modify
 * @param {number} characterLimit - Character limit for truncation
 */
function setupProductDescriptionToggle(
    descriptionElement,
    characterLimit
) {
    const originalContent = descriptionElement.innerHTML;
    const textContent =
        descriptionElement.textContent ||
        descriptionElement.innerText;

    // Create truncated content
    const truncatedText = textContent.substring(
        0,
        characterLimit
    );

    // Find a good breaking point (prefer to break at word boundaries)
    let breakPoint = characterLimit;
    const lastSpaceIndex = truncatedText.lastIndexOf(" ");
    if (lastSpaceIndex > characterLimit * 0.8) {
        breakPoint = lastSpaceIndex;
    }

    const finalTruncatedText = textContent.substring(
        0,
        breakPoint
    );

    // Create wrapper
    const wrapper = document.createElement("div");
    wrapper.className =
        "product-description-toggle is-truncated";

    // Create content container
    const contentContainer = document.createElement("div");
    contentContainer.className = "description-content";

    // Create fade overlay
    const fadeOverlay = document.createElement("div");
    fadeOverlay.className = "description-fade";

    // Create toggle button
    const toggleBtn = document.createElement("button");
    toggleBtn.className = "description-toggle-btn";
    toggleBtn.setAttribute("type", "button");
    toggleBtn.setAttribute("aria-expanded", "false");

    // Remove toggleBtn focus style
    toggleBtn.style.outline = "none";

    // Set initial button text
    updateDescriptionToggleButtonText(toggleBtn, false);

    // Replace original description with new structure
    descriptionElement.parentNode.insertBefore(
        wrapper,
        descriptionElement
    );
    wrapper.appendChild(contentContainer);
    wrapper.appendChild(fadeOverlay);
    wrapper.appendChild(toggleBtn);
    descriptionElement.remove();

    // Set initial truncated content
    contentContainer.textContent =
        finalTruncatedText + "...";

    // Add click event listener to toggle button
    toggleBtn.addEventListener("click", (e) => {
        toggleProductDescription(
            wrapper,
            contentContainer,
            toggleBtn,
            originalContent,
            finalTruncatedText
        );
    });

    // Add click event listener to content container for toggle functionality
    contentContainer.addEventListener("click", (e) => {
        toggleProductDescription(
            wrapper,
            contentContainer,
            toggleBtn,
            originalContent,
            finalTruncatedText
        );
    });
}

/**
 * Toggle product description between truncated and full content
 * @param {HTMLElement} wrapper - The wrapper element
 * @param {HTMLElement} contentContainer - The content container
 * @param {HTMLElement} toggleBtn - The toggle button
 * @param {string} originalContent - The original full content
 * @param {string} truncatedText - The truncated text
 */
function toggleProductDescription(
    wrapper,
    contentContainer,
    toggleBtn,
    originalContent,
    truncatedText
) {
    const isExpanded =
        wrapper.classList.contains("is-expanded");

    if (isExpanded) {
        // Collapse
        wrapper.classList.remove("is-expanded");
        wrapper.classList.add("is-truncated");
        contentContainer.textContent =
            truncatedText + "...";
        toggleBtn.setAttribute("aria-expanded", "false");
    } else {
        // Expand
        wrapper.classList.remove("is-truncated");
        wrapper.classList.add("is-expanded");
        contentContainer.innerHTML = originalContent;
        toggleBtn.setAttribute("aria-expanded", "true");
    }

    updateDescriptionToggleButtonText(
        toggleBtn,
        !isExpanded
    );
}

/**
 * Update the toggle button text based on current state
 * @param {HTMLElement} button - The toggle button
 * @param {boolean} isExpanded - Whether the description is expanded
 */
function updateDescriptionToggleButtonText(
    button,
    isExpanded
) {
    // Get text from translations or use fallback
    let showMoreText = "Ver más";
    let showLessText = "Ver menos";

    // Try to get from theme translations
    if (window.theme?.translations?.product_options) {
        showMoreText =
            window.theme.translations.product_options
                .description_show_more || showMoreText;
        showLessText =
            window.theme.translations.product_options
                .description_show_less || showLessText;
    }

    // Try to get from data attributes
    const moreText = button.getAttribute(
        "data-show-more-text"
    );
    const lessText = button.getAttribute(
        "data-show-less-text"
    );
    if (moreText) showMoreText = moreText;
    if (lessText) showLessText = lessText;

    button.textContent = isExpanded
        ? showLessText
        : showMoreText;
}
